import {
	IconKey,
	IconPlugConnected,
	IconRobot,
	IconTerminal2,
} from "@tabler/icons-react";
import {
	Alert,
	Card,
	Col,
	Flex,
	Modal,
	Row,
	Segmented,
	Space,
	Typography,
	theme,
} from "antd";
import { useEffect, useState } from "react";
import { COPYABLE } from "@/components/base";
import { AgentEnvBlock, CodeBlock } from "./agent-env-block";

export type CliMode = "cli" | "agent" | "mcp" | "machine";

interface CliSetupModalProps {
	open: boolean;
	onClose: () => void;
	initialMode?: CliMode;
}

const MODES = [
	{
		value: "cli" as CliMode,
		Icon: IconTerminal2,
		label: "CLI",
		sub: "You, in a terminal",
	},
	{
		// Renamed from "Local Agent" — that name was ambiguous with the registered External agent
		// kind (run as an agent, ADR-050/ADR-060); this tab is the AI-harness-on-user-credentials setup.
		value: "agent" as CliMode,
		Icon: IconRobot,
		label: "Agent Skill",
		sub: "Local agent, as you",
	},
	{
		value: "mcp" as CliMode,
		Icon: IconPlugConnected,
		label: "MCP",
		sub: "MCP host, as you",
	},
	{
		value: "machine" as CliMode,
		Icon: IconKey,
		label: "Run as an Agent",
		sub: "Unattended",
	},
];

// Public docs hosted alongside the marketing site.
const PROMPT_URL = "https://app.stratonext.com/prompt.md";
// const SKILL_URL = "https://app.stratonext.com/skill.md";
// The CLI bundles the skill, so export it rather than curl-ing skill.md: SKILL.md points at
// a references/ directory, and a single-file download would leave those pointers dangling.
// Self-service External agent: same mutation the Agents wizard uses, minus the round trip
// through the UI — so an agent following prompt.md can register itself.
const EXTERNAL_AGENT_COMMAND =
	'stratonext agent create --name "ci-runner" --kind EXTERNAL';
const AGENT_DOCS_URL =
	"https://docs.stratonext.ai/getting-started/modalities/#your-own-agent";
const SKILL_INSTALL_COMMAND =
	"stratonext --skill-dir .claude/skills/stratonext";
// Paste-into-your-agent instruction: the agent fetches PROMPT.md and self-configures.
const AGENT_PROMPT = `Fetch ${PROMPT_URL} and follow it to install and configure the StratoNext skill in this project.`;

// The CLI bundles an MCP server; `stratonext mcp` serves it over stdio.
const MCP_ADD_COMMAND = "claude mcp add stratonext -- stratonext mcp";
const MCP_JSON_CONFIG = `{
  "mcpServers": {
    "stratonext": {
      "command": "stratonext",
      "args": ["mcp"]
    }
  }
}`;

export function CliSetupModal({
	open,
	onClose,
	initialMode,
}: CliSetupModalProps) {
	const [mode, setMode] = useState<CliMode>(initialMode ?? "cli");
	const [agentSetup, setAgentSetup] = useState<"prompt" | "manual">("prompt");
	const {
		token: { colorPrimary, colorPrimaryBg, colorBorder },
	} = theme.useToken();

	useEffect(() => {
		if (open) setMode(initialMode ?? "cli");
	}, [open, initialMode]);

	const authority = import.meta.env.VITE_AUTH_AUTHORITY ?? "";
	const cliClientId = import.meta.env.VITE_AUTH_CLI_CLIENT_ID ?? "";
	const apiUrl = import.meta.env.VITE_PLATFORM_API_URL ?? "";
	// Resource-server prefix for Cognito scopes, e.g. https://platform.gamma.stratonext.com.
	// Derived from the first custom scope in VITE_AUTH_SCOPE by stripping the /organization:* suffix.
	const audience =
		(import.meta.env.VITE_AUTH_SCOPE ?? "")
			.split(" ")
			.find((s: string) => s.startsWith("https://"))
			?.replace(/\/[^/]+$/, "") ?? "";
	const configBlock = makeConfigBlock({
		authority,
		cliClientId,
		audience,
		apiUrl,
	});

	return (
		<Modal
			title="Connect to StratoNext"
			open={open}
			onCancel={onClose}
			footer={null}
			width={680}
			destroyOnHidden
		>
			<Space vertical size="large" style={{ width: "100%" }}>
				{/* Mode picker */}
				<Row gutter={[8, 8]} align="stretch">
					{MODES.map(({ value, Icon, label, sub }) => {
						const selected = mode === value;
						return (
							<Col key={value} span={6}>
								<Card
									hoverable
									size="small"
									onClick={() => setMode(value)}
									style={{
										height: "100%",
										textAlign: "center",
										cursor: "pointer",
										borderColor: selected ? colorPrimary : colorBorder,
										backgroundColor: selected ? colorPrimaryBg : undefined,
										transition: "border-color 0.2s, background-color 0.2s",
									}}
								>
									<Flex vertical align="center" gap={6}>
										<Icon
											size={32}
											color={selected ? colorPrimary : undefined}
										/>
										<div>
											<Typography.Text strong style={{ display: "block" }}>
												{label}
											</Typography.Text>
											<Typography.Text
												type="secondary"
												style={{ fontSize: 12 }}
											>
												{sub}
											</Typography.Text>
										</div>
									</Flex>
								</Card>
							</Col>
						);
					})}
				</Row>

				{mode === "cli" && (
					<Space vertical style={{ width: "100%" }}>
						<Space vertical>
							<Typography.Text strong style={{ marginBottom: 4 }}>
								Install the CLI
							</Typography.Text>
							<Typography.Paragraph
								code
								copyable={{ ...COPYABLE, text: "uv tool install stratonext" }}
								style={{ margin: 0 }}
							>
								uv tool install stratonext
							</Typography.Paragraph>
						</Space>

						<Space vertical style={{ width: "100%" }}>
							<Typography.Text strong>
								Set up connection settings
							</Typography.Text>
							<CodeBlock>{configBlock}</CodeBlock>
						</Space>

						<Space vertical>
							<Typography.Text strong style={{ marginBottom: 4 }}>
								Log in
							</Typography.Text>
							<Typography.Paragraph
								code
								copyable={{ ...COPYABLE, text: "stratonext login" }}
								style={{ margin: 0 }}
							>
								stratonext login
							</Typography.Paragraph>
						</Space>
					</Space>
				)}

				{mode === "agent" && (
					<Space vertical style={{ width: "100%" }}>
						<Alert
							title={
								<span>
									<strong>Prerequisite</strong>: Complete the CLI setup first
									and log in from the command line.
								</span>
							}
						/>

						<Typography.Text>
							Run any terminal-based AI agent (Claude Code, Codex, etc.) with
							the StratoNext skill. The agent operates with your user
							credentials through the CLI. This is the most capable option: the
							skill documents every command, so the agent can administer the
							platform as well as work in it.
						</Typography.Text>

						<Segmented
							block
							style={{ width: 250 }}
							value={agentSetup}
							onChange={(v) => setAgentSetup(v as "prompt" | "manual")}
							options={[
								{ value: "prompt", label: "Prompt" },
								{ value: "manual", label: "Manual" },
							]}
						/>

						{/* Preferred: hand the agent a prompt and let it configure itself. */}
						{agentSetup === "prompt" && (
							<Space vertical>
								<Typography.Paragraph>
									Paste this into your agent. It fetches the{" "}
									<Typography.Link
										href={PROMPT_URL}
										target="_blank"
										rel="noreferrer"
									>
										setup prompt
									</Typography.Link>{" "}
									and installs the skill for you.
								</Typography.Paragraph>
								<Alert
									type="warning"
									description={
										<Typography.Paragraph
											copyable={{ ...COPYABLE, text: AGENT_PROMPT }}
											style={{ margin: 0 }}
										>
											Fetch{" "}
											<Typography.Link
												href={PROMPT_URL}
												target="_blank"
												rel="noreferrer"
											>
												{PROMPT_URL}
											</Typography.Link>{" "}
											{AGENT_PROMPT.slice(`Fetch ${PROMPT_URL} `.length)}
										</Typography.Paragraph>
									}
								></Alert>
							</Space>
						)}

						{/* Manual: download SKILL.md into the agent's skills dir. */}
						{agentSetup === "manual" && (
							<Space vertical size="small">
								<div>
									Export the skill into your agent's skills directory. It ships
									inside the CLI, so this always matches the version you have
									installed:
								</div>

								<Typography.Paragraph
									code
									copyable={{ ...COPYABLE, text: SKILL_INSTALL_COMMAND }}
								>
									{SKILL_INSTALL_COMMAND}
								</Typography.Paragraph>
							</Space>
						)}
					</Space>
				)}
				{mode === "mcp" && (
					<Space vertical style={{ width: "100%" }}>
						<Alert
							title={
								<span>
									<strong>Prerequisite</strong>: Complete the CLI setup first
									and log in from the command line.
								</span>
							}
						/>

						<Typography.Text>
							The CLI ships an MCP server. Point any MCP host (Claude Code,
							Claude Desktop, Cursor) at it and your agent gets StratoNext as
							typed tools, with no commands to memorise.
						</Typography.Text>

						<Typography.Text>
							This exposes a subset of the platform: the tools needed to carry
							out work. For the full command surface, including administration,
							use the Agent Skill tab instead.
						</Typography.Text>

						<Space vertical>
							<Typography.Text strong>Claude Code</Typography.Text>
							<Typography.Paragraph
								code
								copyable={{ ...COPYABLE, text: MCP_ADD_COMMAND }}
								style={{ margin: 0 }}
							>
								{MCP_ADD_COMMAND}
							</Typography.Paragraph>
						</Space>

						<Space vertical style={{ width: "100%" }}>
							<Typography.Text strong>Other MCP hosts</Typography.Text>
							<CodeBlock>{MCP_JSON_CONFIG}</CodeBlock>
						</Space>

						<Typography.Text>
							The server uses the credentials you logged in with, so it runs as
							you. To run it unattended instead, use Run as an Agent and the
							same server acts as a registered agent.
						</Typography.Text>
					</Space>
				)}

				{mode === "machine" && (
					<Space vertical size="middle" style={{ width: "100%" }}>
						<Typography.Text>
							Run the CLI or MCP server as a registered agent instead of as you.
							Useful for CI/CD and scheduled automation. Requires the CLI setup.
						</Typography.Text>

						<Space vertical>
							<Typography.Text strong>
								1. Register an External agent and save its client secret
							</Typography.Text>
							<Typography.Paragraph
								code
								copyable={{ ...COPYABLE, text: EXTERNAL_AGENT_COMMAND }}
								style={{ margin: 0 }}
							>
								{EXTERNAL_AGENT_COMMAND}
							</Typography.Paragraph>
							<Typography.Text>
								Or create one in the console: Agents → New Agent → External
								Agent.
							</Typography.Text>
						</Space>

						<Space vertical>
							<Typography.Text strong>
								2. Give the credential to the agent's process
							</Typography.Text>
							{/* An agent has no login command: its process gets the credential through these. */}
							<AgentEnvBlock clientId="agtc_…" secret="…" />
							<Typography.Text>
								Set them only in that process's environment, so your own{" "}
								<Typography.Text code>stratonext login</Typography.Text> keeps
								working alongside it.
							</Typography.Text>
						</Space>

						<Typography.Link
							href={AGENT_DOCS_URL}
							target="_blank"
							rel="noreferrer"
						>
							Learn more about running as an agent
						</Typography.Link>
					</Space>
				)}
			</Space>
		</Modal>
	);
}

function makeConfigBlock(vars: {
	authority: string;
	cliClientId: string;
	audience: string;
	apiUrl: string;
}): string {
	const pairs: [string, string][] = [
		["STRATONEXT_AUTH_AUTHORITY", vars.authority],
		["STRATONEXT_AUTH_CLIENT_ID", vars.cliClientId],
		["STRATONEXT_AUTH_AUDIENCE", vars.audience],
		["STRATONEXT_API_URL", vars.apiUrl],
	];
	return pairs.map(([k, v]) => `stratonext config set ${k} "${v}"`).join("\n");
}
